import { cloneElement, useEffect, useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";
import { SERVICES } from "@/data/services";

const CRENEAUX = ["Matin (8h – 12h)", "Après-midi (13h30 – 17h)", "Fin de journée (17h – 19h)"];
const SERVICE_NAMES = [...SERVICES.map((s) => s.nom), "Autre besoin"];

const PRESTATION_CATEGORY = {
  "Visites de surveillance": "surveillance", "Ouverture / fermeture": "cles", "Accueil": "cles",
  "États des lieux": "cles", "Ménage": "entretien", "Blanchisserie": "entretien",
  "Petites réparations": "travaux", "Coordination d'artisans": "travaux",
};

const Field = ({ label, children, required }) => {
  const id = useId();
  return <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}{required && " *"}</Label>
    {cloneElement(children, { id, required })}
  </div>;
};

const Select = ({ value, onChange, options, testid, placeholder, id, required }) => (
  <select id={id} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="flex h-11 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2840]">
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default function DemandeForm({ type }) {
  const [params] = useSearchParams();
  const pre = params.get("prestation") || "";
  const formuleId = type === "devis" ? params.get("formule") : null;
  const { data: formules = [], isLoading: loadingFormules, isError: formulesError } = useQuery({ queryKey: ["formules"], queryFn: () => api.get("/public/formules").then((r) => r.data), enabled: !!formuleId });
  const formule = formules.find((item) => item.id === formuleId);
  const category = SERVICES.find((s) => s.path === params.get("categorie") || s.slug === PRESTATION_CATEGORY[pre]);
  const init = { nom: "", email: "", telephone: "", message: pre ? `Prestation souhaitée : ${pre}\n` : "", prestation: SERVICE_NAMES.includes(pre) ? pre : category?.nom || "", adresse: "", date_souhaitee: "", creneau: "", consentement: false };
  const [f, setF] = useState(init);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [ack, setAck] = useState(false);
  useEffect(() => {
    if (!formule) return;
    setF((s) => ({ ...s, prestation: s.prestation || (formule.slug === "militaire" ? "Conciergerie pour militaires" : formule.slug === "tranquilliser" ? "Surveillance de logement" : "Autre besoin"), message: s.message || `Formule souhaitée : ${formule.nom}\n` }));
  }, [formule]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consentement) return toast.error("Merci d'accepter le traitement de vos données.");
    if (type === "devis" && !f.prestation.trim()) return toast.error("Choisissez le service concerné.");
    if (formuleId && !formule) return toast.error("Cette formule est indisponible. Choisissez une offre dans les tarifs.");
    setLoading(true);
    try {
      const { data } = await api.post("/public/demandes", { ...f, type, formule_id: formuleId || "", prestation_detail: pre });
      setAck(data.acknowledgement_accepted === true);
      setDone(true);
      toast.success("Votre demande a bien été envoyée.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-lg border border-[#0F2840]/10 bg-slate-50 p-10 text-center" data-testid="demande-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-[#0F2840]" />
        <h3 className="mt-4 font-display text-2xl">Merci, votre demande est bien envoyée</h3>
        <p className="mt-2 text-sm text-slate-600">Nous avons enregistré votre demande et nous vous recontactons par téléphone ou par e-mail. {ack ? "L'envoi de votre confirmation a été accepté par le service de messagerie." : "Votre demande reste enregistrée même si vous ne recevez pas de confirmation par e-mail."}</p>
        <button onClick={() => { setF({ ...init, prestation: f.prestation, message: formule ? `Formule souhaitée : ${formule.nom}\n` : init.message }); setDone(false); }} className="mt-6 text-sm font-bold text-[#D82C2C] underline-offset-4 hover:underline" data-testid="demande-new-btn">
          Envoyer une autre demande
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-5 md:grid-cols-2" data-testid={`demande-form-${type}`}>
      {formuleId && <div className="rounded-md border border-slate-200 bg-slate-50 p-4 md:col-span-2" data-testid="demande-formule-summary">
        {loadingFormules ? "Chargement de votre formule…" : formule ? <><strong>{formule.nom} — {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(formule.prix)} / {formule.periode}</strong><p className="mt-1 text-xs text-slate-600">Tarif indicatif du catalogue. Le prix définitif sera confirmé dans votre devis.</p></> : <p role="alert" data-testid="demande-formule-error">{formulesError ? "Impossible de charger la formule." : "Cette formule n'est plus disponible."} <Link to="/tarifs-conciergerie-brest/" className="underline" data-testid="demande-formule-back">Revenir aux tarifs</Link></p>}
      </div>}
      <Field label="Nom complet" required><Input minLength={2} maxLength={120} required value={f.nom} onChange={set("nom")} className="h-11" data-testid="demande-nom-input" /></Field>
      <Field label="Email" required><Input required type="email" value={f.email} onChange={set("email")} className="h-11" data-testid="demande-email-input" /></Field>
      <Field label="Téléphone"><Input value={f.telephone} onChange={set("telephone")} className="h-11" data-testid="demande-telephone-input" /></Field>
      {type !== "contact" && (
        <Field label="Service concerné" required={type === "devis"}>
          <Select value={f.prestation} onChange={set("prestation")} options={SERVICE_NAMES} testid="demande-prestation-select" placeholder="Choisir un service" />
        </Field>
      )}
      {type === "devis" && (
        <Field label="Commune du logement"><Input value={f.adresse} onChange={set("adresse")} placeholder="Ex. : Brest, Guipavas…" className="h-11" data-testid="demande-adresse-input" /></Field>
      )}
      {type === "rdv" && (
        <>
          <Field label="Date souhaitée" required><Input required type="date" min={new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date())} value={f.date_souhaitee} onChange={set("date_souhaitee")} className="h-11" data-testid="demande-date-input" /></Field>
          <Field label="Créneau"><Select value={f.creneau} onChange={set("creneau")} options={CRENEAUX} testid="demande-creneau-select" placeholder="Choisir un créneau" /></Field>
        </>
      )}
      <div className="md:col-span-2">
        <Field label={type === "devis" ? "Décrivez votre besoin (logement, dates, fréquence)" : "Message"} required={type !== "rdv"}>
          <Textarea required={type !== "rdv"} rows={5} value={f.message} onChange={set("message")} data-testid="demande-message-input" />
        </Field>
      </div>
      <label className="flex items-start gap-3 text-xs leading-relaxed text-slate-600 md:col-span-2">
        <Checkbox checked={f.consentement} onCheckedChange={(v) => set("consentement")(!!v)} data-testid="demande-consent-checkbox" className="mt-0.5" />
        <span>
          J'accepte que mes données soient utilisées pour traiter ma demande, conformément à la{" "}
          <Link to="/confidentialite" data-testid="demande-privacy-link" className="font-semibold text-[#0F2840] underline">politique de confidentialité</Link>. *
          {type === "devis" && <> La demande de devis est gratuite et sans engagement ; nos <Link to="/cgv" data-testid="demande-cgv-link" className="font-semibold text-[#0F2840] underline">CGV</Link> vous seront communiquées avec le devis.</>}
        </span>
      </label>
      <div className="md:col-span-2">
        <button type="submit" disabled={loading || (formuleId && !formule)} data-testid="demande-submit-btn"
          className="inline-flex h-12 items-center gap-2 rounded-full bg-[#D82C2C] px-8 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#b82323] disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {type === "devis" ? "Envoyer ma demande de devis" : type === "rdv" ? "Demander ce rendez-vous" : "Envoyer le message"}
        </button>
      </div>
    </form>
  );
}
